export const GLOBAL_ROLES = ['admin', 'team', 'client'] as const;
export type GlobalRole = (typeof GLOBAL_ROLES)[number];

export const PROJECT_ROLES = ['team', 'client_reviewer', 'client_approver'] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];

/** A project's kind: missing = an ordinary project; 'shopify_app' = one app in many stores. */
export const PROJECT_KINDS = ['shopify_app'] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

/**
 * What a team member does on a project — a label that guides assigning and hand-offs (QA testers
 * hear when a fix is ready to verify). It never grants or removes permissions; '' = not set.
 */
export const MEMBER_SPECIALTIES = ['designer', 'developer', 'qa_tester', 'project_manager'] as const;
export type MemberSpecialty = (typeof MEMBER_SPECIALTIES)[number];
// 'designer' is the same role as 'developer': it can no longer be chosen, and one saved before reads as
// Developer everywhere (the stored value is never rewritten).
export const SPECIALTY_LABELS: Record<MemberSpecialty, string> = { designer: 'Developer', developer: 'Developer', qa_tester: 'QA tester', project_manager: 'Project manager' };
/** What can be chosen (Users page, invitations, Members, access requests). */
export const SELECTABLE_SPECIALTIES = ['developer', 'qa_tester', 'project_manager'] as const;
/** A saved 'designer' counts as 'developer'. */
export const normalizeSpecialty = (s: MemberSpecialty): MemberSpecialty => (s === 'designer' ? 'developer' : s);
export const isSpecialty = (s: unknown): s is MemberSpecialty => typeof s === 'string' && (MEMBER_SPECIALTIES as readonly string[]).includes(s);

/**
 * A team account can carry a specialty of its own (User.specialty). On a project, the member's own
 * specialty wins; when it is not set, the account's applies. Computed on read — never copied.
 * Admin and client accounts never have one.
 */
export function effectiveSpecialty(memberRole: unknown, memberSpecialty: unknown, globalRole: unknown, accountSpecialty: unknown): MemberSpecialty | '' {
  if (memberRole !== undefined && memberRole !== null && memberRole !== 'team') return '';
  if (memberRole === 'team' && isSpecialty(memberSpecialty)) return normalizeSpecialty(memberSpecialty);
  return globalRole === 'team' && isSpecialty(accountSpecialty) ? normalizeSpecialty(accountSpecialty) : '';
}

/**
 * Who closed a comment, as they were on that project at that moment (kept with the comment, so a
 * later change of specialty does not change its history): the team's closing is an approval.
 */
export const RESOLVED_AS = ['developer', 'qa_tester', 'project_manager', 'team', 'admin', 'client'] as const;
export type ResolvedAs = (typeof RESOLVED_AS)[number];

/** "Administrator", the team member's specialty ("Developer"), "Team member" or "Client". */
export function roleLabel(globalRole: unknown, specialty?: unknown): string {
  if (globalRole === 'admin') return 'Administrator';
  if (globalRole === 'team') return isSpecialty(specialty) ? SPECIALTY_LABELS[specialty] : 'Team member';
  return 'Client';
}

/**
 * Talk-through review (voice comments on the website). The consent wording has a version: when it
 * changes, everyone is asked again. The limits are checked by the toolbar and by the API.
 */
// 2: the speech service can be Deepgram (through Design Review), not only the browser's own.
export const TALK_CONSENT_VERSION = 2;
export const TALK_LIMITS = {
  clipMaxBytes: 3 * 1024 * 1024,
  clipMaxMs: 180_000,
  maxPoints: 30,
  maxUrls: 5,
  recordingMaxMs: 10 * 60_000,
  clipsPerDay: 300,
  storedBytesPerProject: 200 * 1024 * 1024,
} as const;

/**
 * Voice discussions: a topic talked through at a point on the website (or as a reply), recorded,
 * and turned into a transcript with speakers. Consent has its own version; limits are checked by
 * the toolbar and the API.
 */
export const DISCUSS_CONSENT_VERSION = 1;
export const DISCUSS_LIMITS = {
  maxMs: 60 * 60_000,
  partMaxBytes: 1.5 * 1024 * 1024,
  maxParts: 2000,
  // One recording per page in a discussion across pages (and one more after a lost microphone).
  maxSegments: 60,
  minutesPerUserMonth: 600,
  storedBytesPerProject: 5 * 1024 * 1024 * 1024,
  draftTtlDays: 7,
  maxTranscriptLines: 5000,
  // Pictures taken while talking (where the pointer rests, and each page arrived on), and their size.
  maxMoments: 60,
  momentMaxBytes: 1024 * 1024,
} as const;
export const DISCUSSION_STATUSES = ['recording', 'uploaded', 'transcribing', 'ready', 'failed', 'published', 'discarded'] as const;
export type DiscussionStatus = (typeof DISCUSSION_STATUSES)[number];

export const USER_STATUSES = ['active', 'invited', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const PROJECT_STATUSES = ['active', 'archived', 'deleted'] as const;

export const OPTION_KINDS = ['alternative', 'final_combined'] as const;
export type OptionKind = (typeof OPTION_KINDS)[number];

export const BUILD_STATUSES = ['queued', 'processing', 'ready', 'failed'] as const;
export type BuildStatus = (typeof BUILD_STATUSES)[number];

export const RELEASE_STATES = ['draft', 'published'] as const;
export type ReleaseState = (typeof RELEASE_STATES)[number];

/** Page revision workflow: Draft → In Review → Changes Requested / Approved. */
export const REVISION_STATES = ['draft', 'in_review', 'changes_requested', 'approved'] as const;
export type RevisionState = (typeof REVISION_STATES)[number];

// 'discussion': a recorded voice discussion (audio + transcript), made only by the server.
export const ISSUE_CATEGORIES = ['qa', 'suggestion', 'discussion'] as const;
export type IssueCategory = (typeof ISSUE_CATEGORIES)[number];
/** What a person can pick when writing or re-typing a comment (never 'discussion'). */
export const USER_ISSUE_CATEGORIES = ['qa', 'suggestion'] as const;
export type UserIssueCategory = (typeof USER_ISSUE_CATEGORIES)[number];
/** "QA issue" / "Suggestion" / "Discussion"; short: "QA" / "Idea" / "Discussion". */
export function categoryLabel(c: string | null | undefined, short = false): string {
  if (c === 'qa') return short ? 'QA' : 'QA issue';
  if (c === 'discussion') return 'Discussion';
  return short ? 'Idea' : 'Suggestion';
}

export const ISSUE_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type IssuePriority = (typeof ISSUE_PRIORITIES)[number];

export const ISSUE_STATUSES = ['open', 'in_progress', 'ready_for_review', 'resolved', 'reopened'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];
export const UNRESOLVED_STATUSES: readonly IssueStatus[] = ['open', 'in_progress', 'ready_for_review', 'reopened'];

export const ANCHOR_TYPES = ['element', 'limited', 'page'] as const;
export type AnchorType = (typeof ANCHOR_TYPES)[number];

export const ANCHOR_RESOLUTIONS = ['unverified', 'ok', 'changed', 'missing', 'ambiguous'] as const;
export type AnchorResolution = (typeof ANCHOR_RESOLUTIONS)[number];

export const VISIBILITIES = ['public', 'internal'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

export const ASSIGNMENT_STATUSES = ['pending', 'in_progress', 'completed', 'superseded'] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

export const INTEGRATION_STATUSES = ['not_connected', 'connected', 'authorization_required', 'connection_failed'] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

export const ROUTING_MODES = ['auto', 'history', 'hash'] as const;
export type RoutingMode = (typeof ROUTING_MODES)[number];

// Where the review toolbar sits on the website (chosen for apps inside the Shopify admin, whose own
// floating bar sits at the bottom centre). Default: bottom centre.
export const TOOLBAR_POSITIONS = ['bottom-center', 'bottom-left', 'bottom-right', 'top-center'] as const;
export type ToolbarPosition = (typeof TOOLBAR_POSITIONS)[number];

// site_link: an emailed sign-in link that opens the review toolbar on the website itself.
export const LAUNCH_KINDS = ['preview', 'live', 'sdk_child', 'diagnostic', 'site_link'] as const;
export type LaunchKind = (typeof LAUNCH_KINDS)[number];

export const APPROVAL_TYPES = ['revision_approval', 'changes_requested', 'live_round_signoff'] as const;
export type ApprovalType = (typeof APPROVAL_TYPES)[number];

export const NOTIFICATION_TYPES = [
  'review_requested',
  'mention',
  'reply',
  'issue_assigned',
  'fix_ready',
  'picklist_submitted',
  'revision_approved',
  'changes_requested',
  // Someone asked for access from a reviewed website ("Request access").
  'access_requested',
  // A connected service (e.g. Zoho for the weekly reports) needs an administrator.
  'integration_problem',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'dead'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const PRIORITY_RANK: Record<IssuePriority, number> = { critical: 0, high: 1, medium: 2, low: 3 };
